import { useCallback, useEffect, useRef, useState } from 'react';
import { getPblCase } from '@/api/pbl.api';
import type { PblCase } from '@/types/pbl';

const INTERVAL_MS = 15_000;
const MAX_DURATION_MS = 15 * 60_000;

export const usePblInvitationPolling = (
  caseData: PblCase | null,
  enabled: boolean,
  onRefresh: (next: PblCase, expected: PblCase) => void
) => {
  const current = useRef({ caseData, onRefresh });
  current.current = { caseData, onRefresh };
  const refreshRef = useRef<() => void>();
  const [state, setState] = useState<'active' | 'error' | 'paused' | 'unavailable'>('active');
  const caseId = caseData?.id;
  const waiting = enabled && caseData?.status === 'lawyer_invited';

  useEffect(() => {
    if (!waiting || !caseId) return;
    let active = true;
    let inFlight = false;
    let stopped = false;
    let failures = 0;
    let startedAt = Date.now();
    let timer: number | undefined;
    let controller: AbortController | undefined;
    setState('active');

    const poll = async (manual = false) => {
      if (!active || stopped || inFlight || document.hidden) return;
      window.clearTimeout(timer);
      if (manual) startedAt = Date.now();
      if (Date.now() - startedAt >= MAX_DURATION_MS) {
        setState('paused');
        return;
      }
      const expected = current.current.caseData;
      if (!expected || expected.id !== caseId || expected.status !== 'lawyer_invited') return;
      inFlight = true;
      controller = new AbortController();
      try {
        const response = await getPblCase(caseId, controller.signal);
        if (!active) return;
        if (response.data.success === false) throw new Error('case_refresh_failed');
        // A local action or another request may already have replaced this snapshot.
        if (current.current.caseData !== expected) return;
        failures = 0;
        setState('active');
        current.current.onRefresh(response.data.data, expected);
        stopped = response.data.data.status !== 'lawyer_invited';
      } catch (error: any) {
        if (!active || error?.code === 'ERR_CANCELED') return;
        failures += 1;
        stopped = [401, 403, 404].includes(error?.response?.status);
        setState(stopped ? 'unavailable' : 'error');
      } finally {
        inFlight = false;
        if (active && !stopped) timer = window.setTimeout(() => void poll(), Math.min(60_000, INTERVAL_MS * (failures + 1)));
      }
    };
    const resume = () => { if (!document.hidden) void poll(true); };
    refreshRef.current = resume;
    timer = window.setTimeout(() => void poll(), INTERVAL_MS);
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('focus', resume);
    return () => {
      active = false;
      window.clearTimeout(timer);
      controller?.abort();
      refreshRef.current = undefined;
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('focus', resume);
    };
  }, [caseId, waiting]);

  const refresh = useCallback(() => refreshRef.current?.(), []);
  return { state, refresh, waiting };
};
